import type { Call, Device } from '@twilio/voice-sdk';
import produce from 'immer';
import useObservable from 'packages/workspace/modules/helpers/hooks/useObservable';
import { BehaviorSubject } from 'rxjs';

export const PhoneStates = {
  CONNECTED: 'connected',
};

export interface IPhoneState {
  currentCall: boolean;
  device: Device | null;
  isMuted: boolean;
  isRecording: boolean;
  isHeld: boolean;
  warning: string;
  isRegistered: boolean;
  dialPadNumber: string;
  deviceRequested: boolean;
  connectionStatus: string;
  deviceObtained: boolean;
  active_number: string;
  activeCall?: Call;
}

export const initialPhoneState: IPhoneState = {
  connectionStatus: '',
  currentCall: false,
  device: null,
  isMuted: false,
  isRecording: false,
  isHeld: false,
  warning: '',
  isRegistered: false,
  dialPadNumber: '',
  deviceRequested: false,
  deviceObtained: false,
  active_number: '',
};

const phoneStoreSubject = new BehaviorSubject(initialPhoneState);

export const phoneStore = {
  /**
   * Register a PHONE
   * @param phoneDevice
   */
  registerPhone: (phoneDevice: Device): void => {
    const state = produce(phoneStoreSubject.value, (draftState) => {
      draftState.isRegistered = false;
      draftState.device = phoneDevice;
    });
    phoneStoreSubject.next(state);
  },
  /**
   * Used to request a phone for operator
   */
  requestPhone: (): void => {
    const state = produce(phoneStoreSubject.value, (draftState) => {
      draftState.deviceRequested = true;
    });
    phoneStoreSubject.next(state);
  },
  /**
   * Used to upate connection status
   * @param connection
   */
  phoneConnUpdated: (connection: boolean): void => {
    const state = produce(phoneStoreSubject.value, (draftState) => {
      draftState.currentCall = connection || false;
    });
    phoneStoreSubject.next(state);
  },
  /**
   * Used when connected
   * @param device
   */
  phoneDeviceConnected: (device: Device): void => {
    const state = produce(phoneStoreSubject.value, (draftState) => {
      draftState.connectionStatus = PhoneStates.CONNECTED;
      draftState.device = device;
      draftState.deviceObtained = true;
    });
    phoneStoreSubject.next(state);
  },
  /**
   * Set a conference -used in supervision
   * @param number
   */
  dialConference: async (number: string): Promise<void> => {
    const activeCall = await phoneStoreSubject.value.device?.connect({ params: { conf: number, mute: String(true) } });
    const state = produce(phoneStoreSubject.value, (draftState) => {
      draftState.activeCall = activeCall;
    });
    phoneStoreSubject.next(state);
  },
  /**
   * Used in Centers search
   * @param number
   */
  dialHotlineNumber: (number: string | undefined): void => {
    if (number) {
      const state = produce(phoneStoreSubject.value, (draftState) => {
        draftState.active_number = number;
      });
      phoneStoreSubject.next(state);
    }
  },

  emptyActiveNumber: () : void => {
    const state = produce(phoneStoreSubject.value, (draftState) => {
      draftState.active_number = '';
    });
    phoneStoreSubject.next(state);
  }, 

  disconnectActiveCall: (): void => {
    const state = produce(phoneStoreSubject.value, (draftState) => {
      draftState.currentCall = false;
      draftState.active_number = '';
      draftState.activeCall?.disconnect();
      delete draftState.activeCall;
    });
    phoneStoreSubject.next(state);
  },
};

/**
 * Hook Usage everywhere.
 */

export default function usePhone(): IPhoneState {
  return useObservable<IPhoneState>(phoneStoreSubject, phoneStoreSubject.value);
}
